import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom"; // Removed 'useNavigate'
import { useLanguage } from "../context/LanguageContext";
import "./PaymentPage.css";
import "./ProjectDropdown.css";
import donationLinks from "../utils/DonationLinks";

// Import the project icons
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import NatureIcon from "@mui/icons-material/Nature";
import CakeIcon from "@mui/icons-material/Cake";
import BabyChangingStationIcon from "@mui/icons-material/BabyChangingStation";
import SchoolIcon from "@mui/icons-material/School";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandHoldingHeart } from "@fortawesome/free-solid-svg-icons";

interface Project {
  id: number;
  translationKey: string;
  icon: React.ReactNode;
  slug: string; // Used to look up donation links
}

const PaymentPage: React.FC = () => {
  const [selectedAmount, setSelectedAmount] = useState<number>(0);
  const [projectName, setProjectName] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [currentDonationUrl, setCurrentDonationUrl] = useState<string>("");
  const [showDonationFrame, setShowDonationFrame] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t, language } = useLanguage(); // Now using language to get the correct URL
  const location = useLocation();
  // Removed the unused 'navigate' variable
  const predefinedAmounts = [50, 100, 180, 360, 500];

  // Define available projects - wrapped in useMemo to prevent recreation on every render
  const projects = useMemo(
    () => [
      {
        id: 1,
        translationKey: "project.cleaning",
        icon: <CleaningServicesIcon />,
        slug: "cleaning",
      },
      {
        id: 2,
        translationKey: "project.tubshvat",
        icon: <NatureIcon />,
        slug: "tubshvat",
      },
      {
        id: 3,
        translationKey: "project.batmitzvah",
        icon: <CakeIcon />,
        slug: "batmitzvah",
      },
      {
        id: 4,
        translationKey: "project.baby",
        icon: <BabyChangingStationIcon />,
        slug: "baby",
      },
      {
        id: 5,
        translationKey: "project.school",
        icon: <SchoolIcon />,
        slug: "school",
      },
      {
        id: 6,
        translationKey: "project.chanukah",
        icon: <CurrencyExchangeIcon />,
        slug: "chanukah",
      },
      {
        id: 7,
        translationKey: "project.hospital",
        icon: <LocalHospitalIcon />,
        slug: "hospital",
      },
      {
        id: 8,
        translationKey: "project.soldiers",
        icon: <MilitaryTechIcon />,
        slug: "soldiers",
      },
    ],
    []
  );

  // Helper function to get the donation URL based on current language
  const getDonationUrl = useCallback(
    (type: "general" | "general50" | string, isProject = false) => {
      const langKey = language === "he" ? "hebrew" : "english";

      if (isProject) {
        return (
          donationLinks.projects[type]?.[langKey] ||
          donationLinks.general[langKey]
        );
      } else {
        return (
          donationLinks[type as "general" | "general50"]?.[langKey] ||
          donationLinks.general[langKey]
        );
      }
    },
    [language]
  );

  // Set the default donation URL when component loads or language changes
  useEffect(() => {
    setCurrentDonationUrl(getDonationUrl("general"));
  }, [getDonationUrl]);

  // Extract project information from navigation state if available
  useEffect(() => {
    if (location.state && location.state.projectName) {
      setProjectName(location.state.projectName);

      // Find the matching project
      if (location.state.projectId) {
        const matchingProject = projects.find(
          (project) => project.id === location.state.projectId
        );
        if (matchingProject) {
          setSelectedProject(matchingProject);
        }
      }
    }
  }, [location.state, projects]);

  // Function to update the payerRemarks field in the iframe
  // Memoized with useCallback to avoid dependency cycle
  const updatePayerRemarks = useCallback(() => {
    try {
      if (iframeRef.current && iframeLoaded) {
        // Create a message to send to the iframe
        const message = {
          type: "updatePayerRemarks",
          value: projectName
            ? `${t("donation.for.project")}: ${projectName}`
            : "",
        };

        // Send the message to the iframe - use the current iframe URL origin
        const iframeOrigin = new URL(currentDonationUrl).origin;
        iframeRef.current.contentWindow?.postMessage(message, iframeOrigin);

        // For debugging
        console.log("Sent message to iframe:", message);

        // We also attempt to directly access the iframe's DOM, but this is likely to fail due to cross-origin restrictions
        try {
          const iframeDoc = iframeRef.current.contentWindow?.document;
          if (iframeDoc) {
            const remarksField = iframeDoc.getElementById("payerRemarks");
            if (remarksField) {
              // Cast the element to HTMLInputElement or HTMLTextAreaElement to access the value property
              const inputField = remarksField as
                | HTMLInputElement
                | HTMLTextAreaElement;
              inputField.value = projectName
                ? `${t("donation.for.project")}: ${projectName}`
                : "";
              console.log("Updated remarks field directly:", inputField.value);
            } else {
              console.log("payerRemarks field not found in iframe");
            }
          }
        } catch (error) {
          console.log(
            "Could not access iframe content directly (expected due to CORS):",
            error
          );
        }
      }
    } catch (error) {
      console.error("Error updating payer remarks:", error);
    }
  }, [t, projectName, iframeLoaded, currentDonationUrl]);

  // Update remarks when project changes or iframe loads
  useEffect(() => {
    if (iframeLoaded) {
      updatePayerRemarks();
    }
  }, [iframeLoaded, updatePayerRemarks]);

  // Handle iframe load event
  const handleIframeLoad = () => {
    setIframeLoaded(true);
    // We need a delay to ensure the form elements are fully loaded
    setTimeout(updatePayerRemarks, 2000);
  };

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);

    // Update iframe URL based on selected amount
    if (amount === 50) {
      setCurrentDonationUrl(getDonationUrl("general50"));
      setIframeLoaded(false);
    } else if (amount === 100) {
      setCurrentDonationUrl(getDonationUrl("general100"));
      setIframeLoaded(false);
    } else if (amount === 180) {
      setCurrentDonationUrl(getDonationUrl("general180"));
      setIframeLoaded(false);
    } else if (amount === 360) {
      setCurrentDonationUrl(getDonationUrl("general360"));
      setIframeLoaded(false);
    } else if (amount === 500) {
      setCurrentDonationUrl(getDonationUrl("general500"));
      setIframeLoaded(false);
    } else {
      // For other amounts, revert to the default donation URL
      setCurrentDonationUrl(getDonationUrl("general"));
      setIframeLoaded(false);
    }
  };

  const handleCustomAmount = () => {
    // Use the default donation URL for custom amount
    setCurrentDonationUrl(getDonationUrl("general"));
    setIframeLoaded(false);
    setShowDonationFrame(true);
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setProjectName(t(project.translationKey));
    setIsDropdownOpen(false);

    // If the project slug exists in our donation links, update the iframe and show it
    if (project.slug && donationLinks.projects[project.slug]) {
      // Get language-specific donation URL for the project
      const projectUrl = getDonationUrl(project.slug, true);

      // Update the iframe URL and display it without navigating away
      if (projectUrl) {
        setCurrentDonationUrl(projectUrl);
        setIframeLoaded(false);
        setShowDonationFrame(true);
      }
    }
  };

  const handleDonate = () => {
    setIframeLoaded(false); // Reset iframe loaded state
    setShowDonationFrame(true);
  };

  const handleCloseDonation = () => {
    setShowDonationFrame(false);
    // Reset project selection when closing the donation frame
    setSelectedProject(null);
    setProjectName("");
    // Reset payment completion state
    setPaymentComplete(false);
  };

  // Handle payment completion - this could be triggered by a message from the iframe
  const handlePaymentComplete = () => {
    setPaymentComplete(true);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Set up message listener for communication with the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Try to extract the origin from the current iframe URL
      try {
        const iframeOrigin = new URL(currentDonationUrl).origin;

        // Make sure the message is from our iframe
        if (event.origin === iframeOrigin) {
          console.log("Received message from iframe:", event.data);

          // Handle any response from the iframe if needed
          if (event.data.type === "remarksUpdated") {
            console.log("Remarks updated in iframe:", event.data.success);
          }

          // Listen for payment completion message (you may need to adjust this based on the actual message sent)
          if (
            event.data.type === "paymentComplete" ||
            event.data.type === "transactionComplete"
          ) {
            handlePaymentComplete();
          }
        }
      } catch (error) {
        console.error("Error processing message event:", error);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [currentDonationUrl]);

  return (
    <div className={`payment-page ${language === "en" ? "english" : "hebrew"}`}>
      <div className="payment-header">
        <h1>{t("donation.title")}</h1>
        <p className="payment-subtitle">{t("donation.subtitle")}</p>
      </div>
      <div className="donation-container">
        {!showDonationFrame ? (
          <>
            <div className="donation-options">
              {/* Project selection section */}
              <h2>{t("select.project")}</h2>
              <div className="project-dropdown-container" ref={dropdownRef}>
                <div
                  className="project-dropdown-header"
                  onClick={toggleDropdown}
                >
                  {selectedProject ? (
                    <div className="selected-project">
                      <div className="project-dropdown-icon">
                        {selectedProject.icon}
                      </div>
                      <span>{t(selectedProject.translationKey)}</span>
                    </div>
                  ) : (
                    <span></span>
                  )}
                  <KeyboardArrowDownIcon
                    className={`dropdown-arrow ${isDropdownOpen ? "open" : ""}`}
                  />
                </div>
                {isDropdownOpen && (
                  <div className="project-dropdown-list">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className={`project-dropdown-item ${
                          selectedProject?.id === project.id ? "selected" : ""
                        }`}
                        onClick={() => handleProjectSelect(project)}
                      >
                        <div className="project-dropdown-icon">
                          {project.icon}
                        </div>
                        <span>{t(project.translationKey)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Project banner */}
              {projectName && (
                <div className="project-donation-banner">
                  {t("donating.to.project")}: {projectName}
                </div>
              )}

              {/* Amount selection section directly below */}
              <h2>{t("donation.amount")}</h2>
              <div className="amount-buttons">
                {predefinedAmounts.map((amount) => (
                  <button
                    key={amount}
                    className={`amount-button ${
                      selectedAmount === amount ? "selected" : ""
                    }`}
                    onClick={() => handleAmountSelect(amount)}
                  >
                    ₪{amount}
                  </button>
                ))}
                <button
                  className="amount-button custom"
                  onClick={handleCustomAmount}
                >
                  {t("other.amount")}
                </button>
              </div>

              <button
                className="donate-button"
                onClick={handleDonate}
                disabled={selectedAmount === 0}
              >
                <span className="button-content-wrapper">
                  <FontAwesomeIcon
                    icon={faHandHoldingHeart}
                    className="heart-icon"
                  />
                  <span className="donation-price">
                    {selectedAmount > 0
                      ? `${t("proceed.to.payment.amount")} ${selectedAmount}₪`
                      : t("proceed.to.payment")}
                  </span>
                </span>
              </button>
            </div>
            <div
              className={`donation-info ${
                language === "en" ? "english-text" : ""
              }`}
            >
              <div className="info-card">
                <h3>{t("why.donate")}</h3>
                <p>{t("donation.help.text")}</p>
                <p>{t("donation.impact")}</p>
              </div>
              <div className="info-card">
                <h3>{t("additional.info")}</h3>
                <ul>
                  <li>{t("tax.deductible")}</li>
                  <li>{t("receipt.email")}</li>
                  <li>{t("secure.payment")}</li>
                </ul>
              </div>
            </div>
          </>
        ) : (
          <div className="payment-iframe-container">
            <div className="payment-iframe-header">
              <button
                className="close-iframe-button"
                onClick={handleCloseDonation}
              >
                <CloseIcon />
              </button>
            </div>
            {!iframeLoaded && (
              <div className="iframe-loading">
                <div className="loading-spinner"></div>
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={currentDonationUrl}
              title="Donation Payment Page"
              className="payment-iframe"
              onLoad={handleIframeLoad}
              frameBorder="0"
              allow="payment"
            />

            {/* Exit button that appears after payment is completed */}
            {paymentComplete && (
              <div className="exit-payment-container">
                <button
                  className="exit-payment-button"
                  onClick={handleCloseDonation}
                >
                  {t("return.to.donation.page")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
